import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Search, ExternalLink } from 'lucide-react';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { AdminItem, AdminAddBar } from '../admin/AdminControls';
import type { FieldDef } from '../admin/AdminFieldModal';
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

const MAKEUP_FIELDS: FieldDef[] = [
  { key: 'name', label: 'Name', required: true },
  { key: 'address', label: 'Address', type: 'textarea' },
  { key: 'location', label: 'Location', placeholder: 'Plano' },
  { key: 'image', label: 'Image URL', type: 'image', placeholder: 'https://…' },
  ...SOCIAL_FIELDS,
];

const LOCATION_FILTERS = ['Irving', 'Frisco', 'Plano', 'Dallas', 'Carrollton', 'McKinney', 'Lewisville', 'The Colony', 'Allen', 'Prosper', 'Aubrey', 'Little Elm', 'Melissa', 'FlowerMound', 'Richardson', 'Addison', 'Other'];
const HERO_LOCATIONS = ['All Locations', 'Frisco', 'Plano', 'Irving', 'Lewisville', 'McKinney', 'Dallas', 'Carrollton', 'The Colony', 'Allen', 'Prosper', 'Aubrey', 'Little Elm', 'Melissa', 'FlowerMound', 'Richardson', 'Addison', 'Other'];

export const MAKEUP_STORES = [
  {
    id: 'makeup-1',
    name: 'Lakmé Salon',
    address: '8350 Parkwood Blvd, Plano, TX 75024',
    location: 'Plano',
    image: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=600&h=400&fit=crop',
  },
  {
    id: 'makeup-2',
    name: 'Sephora',
    address: '2601 Preston Rd, Frisco, TX 75034',
    location: 'Frisco',
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=600&h=400&fit=crop',
  },
  {
    id: 'makeup-3',
    name: 'Ulta Beauty',
    address: '7171 N MacArthur Blvd, Irving, TX 75063',
    location: 'Irving',
    image: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=600&h=400&fit=crop',
  },
  {
    id: 'makeup-4',
    name: 'MAC Cosmetics',
    address: '8687 N Central Expy, Dallas, TX 75225',
    location: 'Dallas',
    image: 'https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=600&h=400&fit=crop',
  },
  {
    id: 'makeup-5',
    name: 'Nykaa Beauty',
    address: 'Coming Soon',
    location: 'Frisco',
    image: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=600&h=400&fit=crop',
  },
];

function mapsUrl(name: string, address: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${name}, ${address}`)}`;
}

export default function Makeup() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedHeroLocation, setSelectedHeroLocation] = useState('All Locations');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mc = useManagedCollection<any>('makeup', MAKEUP_STORES, (s, i) => slugify(s.id || s.name || String(i)));

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const effectiveLocation = selectedHeroLocation !== 'All Locations' ? selectedHeroLocation : null;

  const filteredStores = mc.items.filter((store) => {
    const matchesQuery = searchQuery.trim() === '' || store.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesLocation = !effectiveLocation || store.location === effectiveLocation;
    return matchesQuery && matchesLocation;
  }).sort((a: any, b: any) => (a._order ?? 0) - (b._order ?? 0)); // managed order, not alphabetical (keeps edits in place + persists reorder)

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        .makeup-hero-section { height: 185px !important; min-height: 185px !important; }
        .makeup-hero-content { height: 185px !important; min-height: 185px !important; }
        @media (max-width: 1023px) { .makeup-hero-section, .makeup-hero-content { height: 175px !important; min-height: 175px !important; } }
        @media (max-width: 767px) { .makeup-hero-section, .makeup-hero-content { height: 145px !important; min-height: 145px !important; } }
        @media (max-width: 479px) { .makeup-hero-section, .makeup-hero-content { height: 140px !important; min-height: 140px !important; } }
        .filter-scroll::-webkit-scrollbar { display: none; }
        .filter-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Hero */}
      <section
        className="makeup-hero-section relative w-full"
        style={{ backgroundPosition: 'center center', backgroundSize: 'cover' }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center overflow-hidden"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1596462502278-27bfdc403348?w=1600&h=600&fit=crop')" }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/30" />
          <svg viewBox="0 0 1440 24" preserveAspectRatio="none" style={{ position: 'absolute', left: 0, bottom: '-1px', width: '100%', height: '18px', display: 'block', zIndex: 3 }}>
            <path d="M0,0 Q720,24 1440,0 L1440,24 L0,24 Z" fill="#ffffff" />
          </svg>
        </div>
        <div className="makeup-hero-content page-container relative flex items-center justify-center" style={{ zIndex: 9999 }}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="w-full max-w-[740px]">
            <div className="rounded-md" style={{ boxShadow: '0 0 40px 12px rgba(255,255,255,0.45), 0 0 80px 24px rgba(255,255,255,0.2)', backdropFilter: 'blur(4px)' }}>
              <div className="flex flex-col gap-1 sm:gap-0 sm:flex-row bg-transparent sm:bg-white rounded-md sm:shadow-md h-auto sm:h-[40px]">
                <div className="flex-1 px-3 py-2 sm:py-0 flex items-center bg-white rounded-md sm:rounded-none sm:border-r border-gray-200 min-h-[40px]">
                  <span className="text-[11px] text-gray-600 font-semibold shrink-0 mr-2 hidden sm:inline">What are you looking for?</span>
                  <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="e.g. Bridal Makeup, Mehndi, Salon" className="w-full text-[12px] text-gray-700 placeholder:text-gray-400 outline-none bg-transparent" />
                </div>
                <div ref={dropdownRef} className="relative bg-white rounded-md sm:rounded-none sm:border-r border-gray-200 min-h-[40px] sm:w-[170px] md:w-[190px] shrink-0" style={{ zIndex: 99999 }}>
                  <button onClick={() => setShowDropdown(!showDropdown)} className="w-full h-full px-3 py-2 sm:py-0 flex items-center gap-1.5 text-left min-h-[40px]">
                    <MapPin className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <span className="text-[12px] text-gray-700 font-medium truncate flex-1">{selectedHeroLocation}</span>
                    <svg className={'w-3 h-3 text-gray-400 shrink-0 transition-transform ' + (showDropdown ? 'rotate-180' : '')} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showDropdown && (
                    <div
                      className="absolute top-full left-0 bg-white border border-gray-100 rounded-lg py-1 overflow-y-auto"
                      style={{ zIndex: 99999, marginTop: '4px', width: '200px', maxHeight: '220px', boxShadow: '0 10px 24px rgba(15,23,42,0.16)', fontFamily: "'Poppins', sans-serif" }}
                    >
                      {HERO_LOCATIONS.map((loc) => (
                        <button
                          key={loc}
                          onClick={() => { setSelectedHeroLocation(loc); setShowDropdown(false); }}
                          className="w-full text-left flex items-center transition-colors duration-150"
                          style={{
                            padding: '8px 12px',
                            fontSize: '13px',
                            fontWeight: selectedHeroLocation === loc ? 700 : 500,
                            color: selectedHeroLocation === loc ? '#f97316' : '#111827',
                            background: selectedHeroLocation === loc ? '#fff3e8' : 'transparent',
                            height: '36px',
                          }}
                          onMouseEnter={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = '#fff7ed'; }}
                          onMouseLeave={e => { if (selectedHeroLocation !== loc) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button className="flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#061f4a] text-white text-[12px] font-semibold px-5 py-2 sm:py-0 rounded-md sm:rounded-r-md sm:rounded-l-none transition-colors duration-200 min-h-[40px] sm:min-h-0 shrink-0 w-full sm:w-auto">
                  <Search className="w-3.5 h-3.5" />
                  Search
                </button>
              </div>
            </div>

          </motion.div>
        </div>
      </section>
      <NewsTicker />

      <CategoryNav />

      {/* Results */}
      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-6">
        <AdminAddBar mc={mc} fields={MAKEUP_FIELDS} label="Add Store" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredStores.map((store, i) => (
            <AdminItem key={store._key} mc={mc} item={store} fields={MAKEUP_FIELDS} title="Store">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              <div className="w-full h-[160px] relative overflow-hidden">
                <img
                  src={store.image}
                  alt={store.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=600&h=400&fit=crop';
                  }}
                />
              </div>
              <div className="p-4 pb-2 flex-1">
                <h3 className="text-[15px] font-bold text-gray-900 leading-tight truncate mb-1">{store.name}</h3>
                <div className="flex items-center gap-1 mb-1.5">
                  <MapPin className="w-3 h-3 text-pink-500 shrink-0" />
                  <span className="text-[11px] text-gray-500 font-medium">{store.address}</span>
                </div>
              </div>
              <div className="px-4 pb-4 pt-2 flex flex-col gap-2">
                <SocialLinks item={store} px={20} />
                <a
                  href={mapsUrl(store.name, store.address)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1 bg-[#51ba4f] hover:bg-[#43a041] text-white text-[11px] font-semibold py-2 rounded-lg transition-colors duration-200"
                >
                  <ExternalLink className="w-3 h-3" />
                  View Location
                </a>
              </div>
            </motion.div>
            </AdminItem>
          ))}
        </div>
        {filteredStores.length === 0 && (
          <p className="text-center text-gray-500 text-[15px] py-12" style={{ fontFamily: "'Poppins', sans-serif" }}>
            No makeup stores found. Try adjusting your search.
          </p>
        )}
      </div>
    </div>
  );
}
